import { Establishment, Product, WorkingDays } from '@pedix-workspace/utils';

type LocalBusinessEstablishment = Pick<Establishment, 'name' | 'phone' | 'addressDetails' | 'seoSocialMedia' | 'workingDays'>;
type ProductData = Pick<Product, 'name' | 'description' | 'price' | 'priceDiscount' | 'presentations'> & {
  active?: boolean;
};
type ProductEstablishment = Pick<Establishment, 'name' | 'currencyCode'>;

const SCHEMA_DAYS_OF_WEEK: Record<number, string> = {
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
  7: 'Sunday',
};

function formatTime(value: number): string | null {
  const hours = Math.floor(value / 100);
  const minutes = value % 100;
  if (hours > 23 || minutes > 59) return null;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

function buildOpeningHoursSpecification(workingDays: WorkingDays): Record<string, unknown>[] {
  const specs: Record<string, unknown>[] = [];

  for (const day of workingDays.selectedDaysOfWeek ?? []) {
    const dayName = SCHEMA_DAYS_OF_WEEK[day];
    if (!dayName) continue;

    const dayConfig = workingDays.customizeHoursByDay
      ? workingDays.daysOfWeekConfig[day]
      : { primaryWorkingHours: workingDays.primaryWorkingHours, secondaryWorkingHours: workingDays.secondaryWorkingHours };

    if (dayConfig?.primaryWorkingHours?.from != null && dayConfig?.primaryWorkingHours?.to != null) {
      const opens = formatTime(dayConfig.primaryWorkingHours.from);
      const closes = formatTime(dayConfig.primaryWorkingHours.to);
      if (opens && closes) {
        specs.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: dayName,
          opens,
          closes,
        });
      }
    }

    if (dayConfig?.secondaryWorkingHours?.from != null && dayConfig?.secondaryWorkingHours?.to != null) {
      const opens = formatTime(dayConfig.secondaryWorkingHours.from);
      const closes = formatTime(dayConfig.secondaryWorkingHours.to);
      if (opens && closes) {
        specs.push({
          '@type': 'OpeningHoursSpecification',
          dayOfWeek: dayName,
          opens,
          closes,
        });
      }
    }
  }

  return specs;
}

export function buildLocalBusinessJsonLd(
  establishment: LocalBusinessEstablishment,
  establishmentUrl: string,
  cdnLogoUrl: string | null,
): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    '@type': 'LocalBusiness',
    name: establishment.name,
    url: establishmentUrl,
  };

  if (cdnLogoUrl) {
    jsonLd['logo'] = cdnLogoUrl;
    jsonLd['image'] = cdnLogoUrl;
  }

  const description = establishment.seoSocialMedia?.description || establishment.seoSocialMedia?.shortDescription;
  if (description) {
    jsonLd['description'] = description;
  }

  if (establishment.phone) {
    jsonLd['telephone'] = establishment.phone;
  }

  if (establishment.workingDays?.isActive) {
    const specs = buildOpeningHoursSpecification(establishment.workingDays);
    if (specs.length > 0) {
      jsonLd['openingHoursSpecification'] = specs;
    }
  }

  const addr = establishment.addressDetails;
  if (addr) {
    jsonLd['address'] = {
      '@type': 'PostalAddress',
      streetAddress: [addr.street, addr.streetNumber].filter(Boolean).join(' '),
      addressLocality: addr.locality,
      addressRegion: addr.state,
      addressCountry: addr.country,
      postalCode: addr.postalCode,
    };

    if (addr.coordinates) {
      jsonLd['geo'] = {
        '@type': 'GeoCoordinates',
        latitude: addr.coordinates.lat,
        longitude: addr.coordinates.lng,
      };
    }
  }

  return jsonLd;
}

export function buildProductJsonLd(
  product: ProductData,
  establishment: ProductEstablishment,
  productUrl: string,
  cdnImageUrls: string[],
): Record<string, unknown> {
  const jsonLd: Record<string, unknown> = {
    '@type': 'Product',
    name: product.name,
    url: productUrl,
  };

  if (product.description) {
    jsonLd['description'] = product.description;
  }

  if (cdnImageUrls.length > 0) {
    jsonLd['image'] = cdnImageUrls;
  }

  const presentationMinPrice = product.presentations?.items?.reduce((min, item) => {
    const itemPrice = item.priceDiscount != null ? item.priceDiscount : item.price;
    return itemPrice < min ? itemPrice : min;
  }, Number.MAX_VALUE);

  const basePrice = product.priceDiscount != null ? product.priceDiscount : product.price;
  const price = basePrice ?? presentationMinPrice ?? 0;
  const availability =
    product.active === false ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock';

  jsonLd['offers'] = {
    '@type': 'Offer',
    price,
    priceCurrency: establishment.currencyCode,
    availability,
    url: productUrl,
    seller: {
      '@type': 'Organization',
      name: establishment.name,
    },
  };

  return jsonLd;
}

export function buildBreadcrumbJsonLd(
  items: Array<{ name: string; url: string }>,
): Record<string, unknown> {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: item.url,
    })),
  };
}
